import { useCallback, useEffect, useState } from 'react'
import { userHasPremiumSessionRecording } from '@shared/user-settings/flags'
import { useAuthOptional } from '../../../contexts/auth-context'
import { getActiveRepositoryInfo } from '../../sessions/data/sessions-repository-provider'
import type { OrphanRecording } from '../data/recorder-port'
import {
  bindActiveSessionRecording,
  discardOrphanRecording,
  finishOrphanRecording,
  sessionUuidForRecording,
  setCapFinishHandler,
  useActiveSessionRecording,
} from '../hooks/use-active-session-recording'
import { useSessionAudioRecorder } from '../hooks/use-session-recording-adapters'
import { showRecordingToast, useRecordingToast } from '../hooks/use-recording-toast'
import { formatRecordingClock } from '../utils/recording-format'
import { DiscardRecordingDialog } from './rec-bar-dialogs'
import { RecBar, finishToast } from './rec-bar'

function OrphanPrompt({
  orphan,
  sessionUuid,
  busy,
  error,
  onFinish,
  onDiscard,
}: {
  orphan: OrphanRecording
  sessionUuid: string | null
  busy: boolean
  error: string | null
  onFinish: () => void
  onDiscard: () => void
}) {
  return (
    <section
      role="alertdialog"
      aria-modal="false"
      aria-labelledby="orphan-recording-title"
      className="pointer-events-auto w-full max-w-xl rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-950 shadow-xl"
    >
      <h2 id="orphan-recording-title" className="text-sm font-bold">
        Recording stopped unexpectedly at {formatRecordingClock(orphan.elapsedAudioSec)}
      </h2>
      <p className="mt-0.5 text-xs text-amber-900">
        {sessionUuid
          ? 'The app closed while recording. What was recorded is still on this phone.'
          : 'We couldn’t match this recording to one of your sessions, so it can only be discarded.'}
      </p>
      {error && (
        <p className="mt-1 text-xs font-semibold text-red-700" role="alert">
          {error}
        </p>
      )}
      <div className="mt-2 flex flex-wrap gap-2">
        {sessionUuid && (
          <button
            type="button"
            onClick={onFinish}
            disabled={busy}
            className="a11y-touch-target rounded-xl bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 disabled:opacity-50"
          >
            Finish & create note
          </button>
        )}
        <button
          type="button"
          onClick={onDiscard}
          disabled={busy}
          className="a11y-touch-target rounded-xl border border-amber-300 bg-white px-4 py-2 text-sm font-semibold text-amber-900 hover:bg-amber-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 disabled:opacity-50"
        >
          Discard
        </button>
      </div>
    </section>
  )
}

/**
 * App-wide session-recording surfaces (mounted once next to the
 * active-session bar): binds the capture store to the signed-in user and the
 * device recorder, renders the REC bar, the orphan prompt and their toasts.
 *
 * The store binds for any signed-in user on a native recorder so a live
 * capture is never invisible (even if the admin switch went off mid-session);
 * orphan recovery runs only for users who hold the switch.
 */
export function SessionRecordingController() {
  const auth = useAuthOptional()
  const user = auth?.user ?? null
  const userId = user?.userId ?? null
  const premium = userHasPremiumSessionRecording(user) && getActiveRepositoryInfo().mode === 'api'
  const recorder = useSessionAudioRecorder()
  const native = recorder.kind === 'native'
  const recording = useActiveSessionRecording()
  const toast = useRecordingToast()
  const [orphans, setOrphans] = useState<OrphanRecording[]>([])
  const [orphanBusy, setOrphanBusy] = useState(false)
  const [orphanError, setOrphanError] = useState<string | null>(null)
  const [confirmDiscardOrphan, setConfirmDiscardOrphan] = useState(false)

  useEffect(() => {
    if (!userId || !native) return
    return bindActiveSessionRecording(userId, recorder)
  }, [userId, native, recorder])

  // Never keep capturing behind a signed-out screen: pause (audio kept) so the
  // provider can resume or finish after signing back in.
  const signedOut = auth != null && !auth.isLoading && !auth.user
  useEffect(() => {
    if (!signedOut || !native) return
    void recorder
      .status()
      .then((snapshot) => (snapshot.state === 'recording' ? recorder.pause('private_moment') : undefined))
      .catch(() => undefined)
  }, [signedOut, native, recorder])

  useEffect(() => {
    setCapFinishHandler((result) => {
      const toastCopy = finishToast(result)
      showRecordingToast(`Recording stopped at 100 minutes. ${toastCopy.message}`, toastCopy.tone)
    })
    return () => setCapFinishHandler(null)
  }, [])

  useEffect(() => {
    if (!userId || !native || !premium) {
      setOrphans([])
      return
    }
    let cancelled = false
    void recorder.findOrphans().then(
      (found) => {
        if (!cancelled) setOrphans(found)
      },
      () => undefined,
    )
    return () => {
      cancelled = true
    }
  }, [userId, native, premium, recorder])

  const liveId = recording.snapshot.recordingId
  const orphan = orphans.find((entry) => entry.recordingId !== liveId) ?? null
  const orphanSessionUuid = orphan ? sessionUuidForRecording(orphan.recordingId) : null

  const dropOrphan = useCallback((recordingId: string) => {
    setOrphans((list) => list.filter((entry) => entry.recordingId !== recordingId))
    setOrphanError(null)
    setConfirmDiscardOrphan(false)
  }, [])

  const handleFinishOrphan = useCallback(async () => {
    if (!orphan || !orphanSessionUuid) return
    setOrphanBusy(true)
    setOrphanError(null)
    try {
      await finishOrphanRecording(orphan, orphanSessionUuid)
      dropOrphan(orphan.recordingId)
      showRecordingToast('Saved on this phone — uploading')
    } catch {
      setOrphanError('Couldn’t start the upload — try again.')
    } finally {
      setOrphanBusy(false)
    }
  }, [dropOrphan, orphan, orphanSessionUuid])

  const handleDiscardOrphan = useCallback(async () => {
    if (!orphan) return
    setOrphanBusy(true)
    setOrphanError(null)
    try {
      await discardOrphanRecording(orphan, orphanSessionUuid)
      dropOrphan(orphan.recordingId)
      showRecordingToast('Recording discarded')
    } catch {
      setConfirmDiscardOrphan(false)
      setOrphanError('Couldn’t discard the recording — try again.')
    } finally {
      setOrphanBusy(false)
    }
  }, [dropOrphan, orphan, orphanSessionUuid])

  if (!userId) return null

  return (
    <>
      <div className="pointer-events-none fixed inset-x-0 bottom-[calc(4.5rem+var(--safe-bottom))] z-[39] flex flex-col items-center gap-2 px-3 md:bottom-6">
        {toast && (
          <p
            className={`pointer-events-none max-w-md rounded-2xl px-4 py-2 text-center text-sm font-medium text-white shadow-lg ${
              toast.tone === 'error' ? 'bg-red-700/95' : 'bg-slate-900/90'
            }`}
            role={toast.tone === 'error' ? 'alert' : 'status'}
            aria-live={toast.tone === 'error' ? 'assertive' : 'polite'}
          >
            {toast.message}
          </p>
        )}
        {orphan && !confirmDiscardOrphan && (
          <OrphanPrompt
            orphan={orphan}
            sessionUuid={orphanSessionUuid}
            busy={orphanBusy}
            error={orphanError}
            onFinish={() => void handleFinishOrphan()}
            onDiscard={() => setConfirmDiscardOrphan(true)}
          />
        )}
        <RecBar notify={showRecordingToast} />
      </div>
      {orphan && confirmDiscardOrphan && (
        <DiscardRecordingDialog
          busy={orphanBusy}
          onCancel={() => setConfirmDiscardOrphan(false)}
          onConfirm={() => void handleDiscardOrphan()}
          description="What was recorded is deleted from this phone and nothing is uploaded. This can’t be undone."
        />
      )}
    </>
  )
}
